import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// the real session is an httpOnly cookie set by the server; the browser sends it automatically
axios.defaults.withCredentials = true;

const ROLE_KEY = "vikas_role";
const ACT_KEY = "vikas_can_act";
const EXP_KEY = "vikas_exp";
const SESSION_HOURS = 12;

// only non-secret UI hints live here; NO token is ever stored in page-readable storage
export function getSession() {
  const role = localStorage.getItem(ROLE_KEY);
  if (!role) return null;
  const exp = Number(localStorage.getItem(EXP_KEY) || 0);
  if (exp && Date.now() > exp) { clearSession(); return null; }
  return { role, canAct: localStorage.getItem(ACT_KEY) === "true" };
}

export function saveSession(s) {
  localStorage.setItem(ROLE_KEY, s.role);
  localStorage.setItem(ACT_KEY, String(s.can_act));
  const exp = s.exp ? Number(s.exp) : Date.now() + SESSION_HOURS * 3600 * 1000;
  localStorage.setItem(EXP_KEY, String(exp));
}

export function clearSession() {
  localStorage.removeItem(ROLE_KEY);
  localStorage.removeItem(ACT_KEY);
  localStorage.removeItem(EXP_KEY);
}

// confirm the httpOnly cookie is still valid (used on reload to restore the 12h session)
export async function validateSession() {
  try { const res = await axios.get(`${API}/me`); return res.data; }
  catch { clearSession(); return null; }
}

function authHeader() {
  return {};
}




// Google sign-in: exchange the one-time session_id for our own httpOnly session cookie
export async function googleSignIn(session_id) {
  const res = await axios.post(`${API}/auth/google`, { session_id });
  return res.data;
}

export async function logout() {
  try { await axios.post(`${API}/logout`); } catch { /* cookie may already be gone */ }
}

// when the board last received an upload from the feed (server header, ISO time)
let receivedAt = "";
export const getReceivedAt = () => receivedAt;

export async function fetchData() {
  const res = await axios.get(`${API}/data`, { headers: authHeader() });
  receivedAt = res.headers?.["x-data-received"] || "";
  return res.data;
}

// server-persisted "received" badges: { "panel:target_id": { by, at } }
export async function fetchRecv() {
  try { const res = await axios.get(`${API}/recv`, { headers: authHeader() }); return res.data?.recv || {}; }
  catch { return {}; }
}

export async function getUsers() {
  const res = await axios.get(`${API}/users`, { headers: authHeader() });
  return res.data.users || [];
}

export async function postAction(payload) {
  const res = await axios.post(`${API}/action`, payload, { headers: authHeader() });
  return res.data;
}

export async function uploadData(file) {
  const form = new FormData();
  form.append("file", file);
  const res = await axios.post(`${API}/data/upload`, form, {
    headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function fetchActions(since) {
  const q = since ? `?since=${encodeURIComponent(since)}` : "?format=json";
  const res = await axios.get(`${API}/actions${q}`, { headers: authHeader() });
  return res.data;
}

export async function deleteAction(id) {
  const res = await axios.delete(`${API}/action/${encodeURIComponent(id)}`, { headers: authHeader() });
  return res.data;
}



export async function downloadActions() {
  const res = await axios.get(`${API}/actions`, {
    headers: authHeader(),
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "vikas-dashboard-actions.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// ---- display helpers ----
const NM_TOKENS = ["NOT MEASURED", "NO SOURCE YET", "NOT YET"];

export function isNM(v) {
  return typeof v === "string" && NM_TOKENS.some((t) => v.toUpperCase().startsWith(t));
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DATE_RE = /(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/g;

export function fmtDates(s) {
  if (typeof s !== "string") return s;
  return s.replace(DATE_RE, (m, y, mo, dd, hh, mm) => {
    const base = `${parseInt(dd, 10)} ${MONTHS[parseInt(mo, 10) - 1]}`;
    return hh ? `${base} · ${hh}:${mm}` : base;
  });
}

export function fmtDay(s) {
  if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}/.test(s)) return s || "";
  const mo = parseInt(s.slice(5, 7), 10);
  return `${parseInt(s.slice(8, 10), 10)} ${MONTHS[mo - 1]}`;
}

const INTERNAL_WS = new Set(["SYSTEM", "TEAM"]);
export function isBusiness(ws) {
  return !INTERNAL_WS.has((ws || "").toUpperCase());
}

// business/client rows first, then oldest first (by _date)
export function cmpImportance(a, b) {
  const d = (isBusiness(b.workstream) ? 1 : 0) - (isBusiness(a.workstream) ? 1 : 0);
  if (d !== 0) return d;
  const da = a._date || "";
  const db = b._date || "";
  return da < db ? -1 : da > db ? 1 : 0;
}

export function truncate8(s) {
  const w = String(s || "").split(/\s+/).filter(Boolean);
  return w.length > 8 ? w.slice(0, 8).join(" ") + " …" : w.join(" ");
}

export function truncate6(s) {
  const w = String(s || "").split(/\s+/).filter(Boolean);
  return w.length > 6 ? w.slice(0, 6).join(" ") + " …" : w.join(" ");
}

export function sentenceCase(s) {
  const str = String(s || "");
  const letters = str.replace(/[^a-zA-Z]/g, "");
  if (letters.length > 3 && letters === letters.toUpperCase()) {
    const low = str.toLowerCase();
    return low.charAt(0).toUpperCase() + low.slice(1);
  }
  return str;
}

export function greetingWord() {
  let h;
  try {
    h = parseInt(
      new Intl.DateTimeFormat("en-GB", { hour: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }).format(new Date()),
      10
    );
  } catch {
    h = new Date().getHours();
  }
  return h < 12 ? "morning" : h < 17 ? "afternoon" : "evening";
}

// "NOT SETTLED" / "NOT MEASURED" fields render as a soft grey "not set"
export function softVal(v) {
  if (typeof v === "string") {
    const u = v.toUpperCase();
    if (u.startsWith("NOT SETTLED") || u.startsWith("NOT MEASURED") || u.startsWith("NO SOURCE YET")) return "not set";
  }
  return v;
}

// ---- CARDS V2: groups, colours, card helpers ----

export const GROUP_LIST = ["Renevik", "Vision2075", "System"];
export const GROUP_COLORS = {
  Renevik: "#fbf069",
  Vision2075: "#2dd4bf",
  System: "#a58af3",
};

const VISION_WS = new Set(["VISION2075", "SECURITIES", "MUTUAL FUNDS"]);
const SYSTEM_WS = new Set(["SYSTEM", "TEAM"]);

// E: explicit row.group wins; else map from workstream.
export function groupOf(raw) {
  const g = (raw && raw.group) ? String(raw.group).trim() : "";
  if (g) {
    const u = g.toLowerCase();
    if (u === "vision2075" || u === "vision 2075") return "Vision2075";
    if (u === "system") return "System";
    if (u === "renevik") return "Renevik";
    return g;
  }
  const ws = String((raw && raw.workstream) || "").toUpperCase();
  if (SYSTEM_WS.has(ws)) return "System";
  if (VISION_WS.has(ws)) return "Vision2075";
  return "Renevik";
}

// line-2 tag: max 14 chars + ellipsis
export function truncChars(s, n = 14) {
  const str = String(s || "").trim();
  if (str.length <= n) return str;
  return str.slice(0, n).trim() + "…";
}

// line-1 title: never cut mid-word (word-boundary truncation)
export function clampWords(s, n = 14) {
  const w = String(s || "").split(/\s+/).filter(Boolean);
  return w.length > n ? w.slice(0, n).join(" ") + " …" : w.join(" ");
}

// plain-words status (B3)
const STATUS_WORDS = {
  open: "not done yet",
  running: "being worked on",
  review: "being checked",
  ready: "queued to build",
  "viable?": "being assessed",
  viable: "being assessed",
  idea: "just an idea",
  ideas: "just an idea",
  blocked: "stuck",
  parked: "parked",
  held: "held",
  "held-notified": "held",
  sent: "sent",
  done: "done",
};
export function statusWords(s) {
  if (!s) return "";
  return STATUS_WORDS[String(s).toLowerCase()] || String(s);
}

// the date a row is anchored to (for date-range filtering)
export function cardDateVal(raw) {
  return raw.when || raw.due || raw.updated || raw.queued || raw.raised || raw.received || raw.date || raw.sent || "";
}

// best human "when" for the card face
export function cardWhen(raw) {
  const d = raw.due || raw.raised || raw.received || raw.date || raw.queued || raw.updated || "";
  return d ? fmtDay(d) : "";
}

// F: priority comparator. rank asc wins; else days_late desc, tier T0..T3, oldest.
const TIER_RANK = { T0: 0, T1: 1, T2: 2, T3: 3 };
export function cmpPriority(a, b) {
  const ra = a.rank, rb = b.rank;
  const hasA = ra !== undefined && ra !== null && ra !== "";
  const hasB = rb !== undefined && rb !== null && rb !== "";
  if (hasA || hasB) {
    if (hasA && hasB) return Number(ra) - Number(rb);
    return hasA ? -1 : 1;
  }
  const dla = Number(a.days_late || 0), dlb = Number(b.days_late || 0);
  if (dla !== dlb) return dlb - dla;
  const ta = TIER_RANK[a.tier] ?? 9, tb = TIER_RANK[b.tier] ?? 9;
  if (ta !== tb) return ta - tb;
  const da = a._date || "", db = b._date || "";
  return da < db ? -1 : da > db ? 1 : 0;
}

// "1 card" / "2 cards"
export const plural = (n, one, many) => `${n} ${Number(n) === 1 ? one : many}`;
